/**
 *
 * DownloadCertificateButton
 *
 */

import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import _get from 'lodash/get';
import CircularProgress from '@material-ui/core/CircularProgress';
import useSiteCopySelector from 'components/useSiteCopySelector';
import AssessmentResultContext from 'containers/AssessmentResult/AssessmentResultContext';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import { makeSelectAuth } from 'containers/Auth/selectors';
import { moveAnonymousAssessments } from 'containers/Assessment/actions';
import Mixpanel, { injectAssessmentResultsABData } from 'utils/mixpanelService';
import useAssessmentCertificate from '../hooks/useAssessmentCertificate';

const useStyles = makeStyles(theme => ({
  button: {
    display: 'flex',
    padding: '12px 40px',
    borderRadius: 4,
    justifyContent: 'center',
    marginLeft: 8,
    marginRight: 8,
    textTransform: 'none',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      width: '100%',
      marginTop: 16,
    },
    ...theme.typography.caption,
  },
  tight: {
    padding: '10px 15px',
    minWidth: 'unset',
    fontSize: '0.875rem',
    lineHeight: '1.5',
    fontFamily: 'Mada',
    fontWeight: 700,
    textTransform: 'none',
    height: 'fit-content',
    border: `1px solid ${theme.palette.primary.main}`,
  },
  disabled: {
    border: '1px solid #E0E0E0 !important',
  },
}));

function DownloadCertificateButton({
  answer,
  assessment,
  tight = false,
  variant = 'outlined',
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const auth = useSelector(makeSelectAuth());
  const { isVariantB, shouldABTest } = useContext(AssessmentResultContext);
  const [assessmentResultsSiteCopy] = useSiteCopySelector([
    'assessment-results',
  ]);
  const { slug } = useParams();
  const [isEnabled, processing, certificateLink] = useAssessmentCertificate(
    answer,
  );

  if (!isEnabled) return null;

  const assessmentID = _get(answer, 'assessment.assessmentID', '');
  const name = _get(answer, 'assessment.name', '');

  const isAuthenticated = auth.isLoaded && !auth.isEmpty;

  const trackCertificateClick = () => {
    Mixpanel.track(
      'Download Certificate Clicked',
      injectAssessmentResultsABData(
        {
          path: window.location.pathname,
          type: 'assessment',
          slug,
          name,
          assessmentId: assessmentID,
          overallResultId: _get(answer, 'resultID.overall', null),
          source: 'action-buttons',
        },
        shouldABTest,
        isVariantB,
      ),
    );
  };

  const handleAnonymousClick = () => {
    dispatch(moveAnonymousAssessments({ certificate: true, assessment }));
  };

  if (isAuthenticated) {
    if (!processing && !certificateLink) return null;

    if (processing) {
      return (
        <Button
          variant={variant}
          color="primary"
          className={_classNames(classes.button, classes.disabled, {
            [classes.tight]: tight,
          })}
          disabled
          aria-label="Download Certificate"
          startIcon={<CircularProgress size={20} />}
        >
          {_get(assessmentResultsSiteCopy, 'pageCopy.downloadCertificateLabel')}
        </Button>
      );
    }

    return (
      <Button
        variant={variant}
        color="primary"
        className={_classNames(classes.button, {
          [classes.tight]: tight,
          [classes.disabled]: processing,
        })}
        component="a"
        disableRipple
        disableFocusRipple
        disabled={processing}
        aria-label="Download Certificate"
        href={certificateLink}
        onClick={trackCertificateClick}
        target="_blank"
        download
      >
        {processing && <CircularProgress />}
        {_get(assessmentResultsSiteCopy, 'pageCopy.downloadCertificateLabel')}
      </Button>
    );
  }

  return (
    <Button
      variant={variant}
      color="primary"
      className={_classNames(classes.button, { [classes.tight]: tight })}
      onClick={handleAnonymousClick}
    >
      {_get(assessmentResultsSiteCopy, 'pageCopy.downloadCertificateLabel')}
    </Button>
  );
}

DownloadCertificateButton.propTypes = {
  answer: PropTypes.object,
  assessment: PropTypes.object,
  tight: PropTypes.bool,
  variant: PropTypes.string,
};

export default DownloadCertificateButton;
